/**
 * Return the translated value for the given locale from `content.contentItems`,
 * falling back to `fallback` (e.g. the entity's base/English name) when no
 * matching content item exists.
 *
 * Accepts a full locale (e.g. 'en_US') or a bare language code (e.g. 'en') -
 * only the language portion before the underscore is used to match against
 * ContentItem.language.
 *
 * Used to resolve admin-authored, per-locale translations (e.g. attestation
 * text) that come from content_items rather than Weglot's client-side
 * translation.
 */
export const getLocalizedContentValue = (content, locale, fallback) => {
    // Our locale contract is underscore-delimited (e.g. 'en_US') everywhere else
    // in the app - see pe-core-frontend's `LANGUAGES` config. A hyphenated
    // locale reaching here would mean something upstream produced a malformed
    // value; splitting on either separator is just cheap insurance so that bug
    // degrades to a fallback-string lookup here rather than a silent mismatch,
    // it doesn't make '-' a second supported locale format.
    const language = locale?.split(/[-_]/)[0]?.toLowerCase();
    if (content?.contentItems && language) {
        const localized = content.contentItems.find(item => item.language.toLowerCase() === language);
        if (localized) {
            return localized.value;
        }
        // `fallback` (e.g. content.name) is just an admin-facing label, not
        // necessarily English body text - prefer the English content item, if
        // one exists, before falling back to it.
        const english = content.contentItems.find(item => item.language.toLowerCase() === 'en');
        if (english) {
            return english.value;
        }
    }
    return fallback;
};
